import "./styles/main.css";
import "./styles/flags.css";
import React from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { createBrowserHistory } from "history";
import { PersistGate } from "redux-persist/integration/react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CookiesProvider } from "react-cookie";

import { Translator } from "libs";
import createStore from "./store/createStore";
import ChatSocketManager from "./libs/chatSocketManager";
import { ICustomWindow } from "./types";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/de";
import "moment/locale/fr";
import "moment/locale/it";
import { name, version } from "./../package.json";
import Dragscroll from "dragscroll";
import App from "./components/app";
import "react-datez/dist/css/react-datez.css";
import loadScript from "./libs/loadScript";
import { hasCookie, getCookie } from "./libs/cookies";
import * as Sentry from "@sentry/browser";
import * as serviceWorker from "./serviceWorker";

import LogRocket from "logrocket";
import setupLogRocketReact from "logrocket-react";
import { getCookieLang, getShortLang } from "./libs/detectLanguage";
import { TokenService } from "./libs/tokenService";
import DisplayModeContext, { DisplayMode } from "./contexts/displayModeContext";
import { Splashscreen } from "components";
import allSettled from "promise.allsettled";
import { parseHashArray } from "./libs/parseHashArray";
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getRemoteConfig } from "firebase/remote-config";
import { createFirebaseConfig } from "./contexts/firebaseContext";
import { defaultRemoteConfig } from "./contexts/remoteConfigContext";
import ActivateCookies from "./components/activateCookies";

allSettled.shim();

Dragscroll.reset();

const fbApp = initializeApp(createFirebaseConfig());
const analytics = getAnalytics(fbApp);
const remoteConfig = getRemoteConfig(fbApp);
remoteConfig.defaultConfig = defaultRemoteConfig;
remoteConfig.settings = {
	fetchTimeoutMillis: 10000,
	minimumFetchIntervalMillis: 3600000,
};

/*
fails in private mode
(async function() {
	try {
		await remoteConfig.fetchAndActivate();
	} catch (err) {
		console.log({ err });
	}
})();
*/
Sentry.init({
	dsn: "https://eca0e26c98a142fc8245d812f86e5176@sentry.io/1525424",
	enabled: process.env.NODE_ENV !== "development",
	environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
	release: `${name}@${version}`,
});

if (process.env.NODE_ENV !== "development") {
	LogRocket.init(process.env.REACT_APP_LOGROCKET_ID);
	setupLogRocketReact(LogRocket);

	LogRocket.getSessionURL((sessionURL) => {
		Sentry.configureScope((scope) => {
			scope.setExtra("sessionURL", sessionURL);
		});
	});
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const myWindow: ICustomWindow = window;

const lang = getCookieLang() || getShortLang() || "en";
moment.locale(lang);

const translator = new Translator(lang);

Translator.register(window, translator);

myWindow.loadLang = (lang: string): Promise<void> => {
	//https://wanago.io/2018/08/20/webpack-4-course-part-eight-dynamic-imports-with-prefetch-and-preload/
	return import(`./locales/${lang}.json` /* webpackPreload: true */)
		.then((translations) => {
			translator.load(lang, translations.default);
		})
		.catch((err) => {
			console.warn("could not load translations", err);
		});
};

// Awaited before the first render so screens that never re-render (e.g. the
// storage access prompt) do not get stuck showing raw translation keys.
const translationsLoaded = myWindow.loadLang(lang);

const history = createBrowserHistory({
	basename: process.env.REACT_APP_BASE_PATH,
});

const chatSocketManager = new ChatSocketManager(process.env.REACT_APP_SOCKET_IO_ENDPOINT);

const { clubIds } = parseHashArray(window.location.hash);

const displayMode = clubIds && clubIds.length > 0 ? DisplayMode.WIDGET : DisplayMode.STANDALONE;

if (displayMode === DisplayMode.WIDGET) {
	chatSocketManager.disable();
}

const STORE_KEY_COOKIE_NAME = "gc_player_uuid";
let storeKey = "player";
let tokenService = null;

const initStore = () => {
	if (hasCookie(STORE_KEY_COOKIE_NAME) && process.env.NODE_ENV !== "development") {
		storeKey = getCookie(STORE_KEY_COOKIE_NAME);
	}

	const { store, persistor } = createStore(history, chatSocketManager, storeKey);

	myWindow.store = store;
	chatSocketManager.setStore(store);

	tokenService = new TokenService(store);
	myWindow.tokenService = tokenService;

	document.addEventListener("visibilitychange", function () {
		const visibilityState = document.visibilityState;
		store.dispatch({ type: "VISIBILITY_CHANGE", visibilityState });
	});

	return { store, persistor };
};

const key = process.env.REACT_APP_GMAPS_KEY;
const promise = loadScript(
	`https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,geometry&language=${lang}`,
	"gmapsjs"
);

const hasCookiesEnabled = async () => {
	//@ts-ignore
	if (document.hasStorageAccess && document.requestStorageAccess) {
		return await document
			//@ts-ignore
			.hasStorageAccess()
			.then((hasAccess) => hasAccess)
			.catch((_) => false);
	}
	return navigator.cookieEnabled;
};

function loadApp(existingStore = null) {
	promise.then(() => {
		const { store, persistor } = existingStore ?? initStore();

		render(
			<Provider store={store}>
				<PersistGate loading={<Splashscreen />} persistor={persistor}>
					<Elements stripe={stripePromise}>
						<DisplayModeContext.Provider value={displayMode}>
							<CookiesProvider>
								<App
									{...{
										history,
										chatSocketManager,
										dispatch: store.dispatch,
										tokenService,
										remoteConfig,
									}}
									googleAppId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
								/>
							</CookiesProvider>
						</DisplayModeContext.Provider>
					</Elements>
				</PersistGate>
			</Provider>,
			document.getElementById("root")
		);

		if (process.env.NODE_ENV !== "development") {
			serviceWorker.register({
				onSuccess: (registration) => {
					console.log({ registration });
				},
			});
		}
	});
}

Promise.all([hasCookiesEnabled(), translationsLoaded]).then(([hasCookiesEnabled]) => {
	const store = initStore();
	if (!hasCookiesEnabled) {
		render(<ActivateCookies callback={loadApp} />, document.getElementById("root"));
	} else {
		loadApp(store);
	}
});
